import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Fab,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { ContactBanner, SiteFooter } from './Home';
import axios from 'axios';

interface BlogArticle {
  id: number;
  title: string;
  excerpt: string;
  content?: string;
  image_url: string;
  alt_text?: string;
  author: string;
  is_published: boolean;
  is_featured: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
  category?: string;
}

const BlogGallery = () => {
  const { category: rawCategory } = useParams<{ category: string }>();
  const navigate = useNavigate();
  
  // Décoder la catégorie de l'URL
  const category = rawCategory ? decodeURIComponent(rawCategory) : '';
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // États pour l'admin
  const [isAdmin, setIsAdmin] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<BlogArticle | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMethod, setUploadMethod] = useState<'upload' | 'url'>('url');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image_url: '',
    alt_text: '',
    author: 'La Petite Académie',
    is_featured: false,
    category: ''
  });

  useEffect(() => {
    console.log('🔍 BlogGallery - Catégorie reçue:', {
      rawCategory,
      decodedCategory: category
    });
    checkAdminStatus();
    loadArticles();
  }, [category]);

  const checkAdminStatus = () => {
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('userRole');
    setIsAdmin(!!token && userRole === 'secretary');
  };

  const loadArticles = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://lapetiteacademie.ci/api/dynamic-pages/blog/articles');
      
      if (response.data.status === 'success') {
        const allArticles = response.data.data || [];
        
        // Filtrer par catégorie si spécifiée
        let filteredArticles = allArticles;
        if (category === 'articles') {
          // Articles de blog normaux (non featured)
          filteredArticles = allArticles.filter((article: BlogArticle) => !article.is_featured);
        } else if (category === 'actualites') {
          // Actualités (articles à la une)
          filteredArticles = allArticles.filter((article: BlogArticle) => article.is_featured);
        } else if (category && category !== 'articles' && category !== 'actualites') {
          // Filtrer par la vraie catégorie de l'article
          filteredArticles = allArticles.filter((article: BlogArticle) => {
            const articleCategory = article.category || 'Articles Généraux';
            return articleCategory === category;
          });
        }
        
        const uniqueCategories = [...new Set(allArticles.map((article: BlogArticle) => article.category || 'Articles Généraux'))];
        console.log('🔍 Filtrage des articles:', {
          category,
          totalArticles: allArticles.length,
          filteredArticles: filteredArticles.length,
          categories: uniqueCategories
        });
        
        setArticles(filteredArticles);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imageUrl: string) => {
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    if (imageUrl.startsWith('/img/pages/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    if (imageUrl.startsWith('/uploads/pages/')) {
      return `https://lapetiteacademie.ci/api/dynamic-pages/images/${imageUrl.split('/').pop()}`;
    }
    
    if (imageUrl.startsWith('/uploads/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    return `https://lapetiteacademie.ci${imageUrl}`;
  };

  const getCategoryTitle = () => {
    if (category === 'articles') return 'Articles de Blog';
    if (category === 'actualites') return 'Actualités';
    if (category && category !== 'articles' && category !== 'actualites') {
      return `Articles - ${category}`;
    }
    return 'Blog';
  };

  const handleAddArticle = () => {
    setEditingItem(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      image_url: '',
      alt_text: '',
      author: 'La Petite Académie',
      is_featured: category === 'actualites',
      category: ''
    });
    setSelectedFile(null);
    setUploadMethod('url');
    setOpenDialog(true);
  };

  const handleEditArticle = (article: BlogArticle) => {
    setEditingItem(article);
    setFormData({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content || '',
      image_url: article.image_url,
      alt_text: article.alt_text || '',
      author: article.author,
      is_featured: article.is_featured,
      category: article.category || ''
    });
    setSelectedFile(null);
    setUploadMethod('url');
    setOpenDialog(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner un fichier image valide');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('La taille du fichier ne doit pas dépasser 5MB');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.excerpt) {
      setError('Titre et extrait requis');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': uploadMethod === 'upload' ? 'multipart/form-data' : 'application/json'
      };

      let imageUrl = formData.image_url;

      if (uploadMethod === 'upload' && selectedFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('image', selectedFile);
        Object.keys(formData).forEach(key => {
          if (key !== 'image_url') {
            formDataUpload.append(key, (formData as any)[key]);
          }
        });

        const endpoint = editingItem
          ? `https://lapetiteacademie.ci/api/dynamic-pages/blog/articles/${editingItem.id}/upload`
          : `https://lapetiteacademie.ci/api/dynamic-pages/blog/articles/upload`;

        const response = editingItem
          ? await axios.put(endpoint, formDataUpload, { headers })
          : await axios.post(endpoint, formDataUpload, { headers });

        if (response.data.status === 'success') {
          setSuccess(editingItem ? 'Article mis à jour avec succès' : 'Article ajouté avec succès');
          setOpenDialog(false);
          loadArticles();
        }
      } else {
        const data = { ...formData, image_url: imageUrl };

        const endpoint = editingItem
          ? `https://lapetiteacademie.ci/api/dynamic-pages/blog/articles/${editingItem.id}`
          : `https://lapetiteacademie.ci/api/dynamic-pages/blog/articles`;

        const response = editingItem
          ? await axios.put(endpoint, data, { headers })
          : await axios.post(endpoint, data, { headers });

        if (response.data.status === 'success') {
          setSuccess(editingItem ? 'Article mis à jour avec succès' : 'Article ajouté avec succès');
          setOpenDialog(false);
          loadArticles();
        }
      }
    } catch (error: any) {
      console.error('Erreur lors de la soumission:', error);
      setError(error.response?.data?.message || 'Erreur lors de la soumission');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteArticle = async (articleId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `https://lapetiteacademie.ci/api/dynamic-pages/blog/articles/${articleId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSuccess('Article supprimé avec succès');
      loadArticles();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      setError(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleTogglePublish = async (article: BlogArticle) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `https://lapetiteacademie.ci/api/dynamic-pages/blog/articles/${article.id}`,
        { is_published: !article.is_published },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setSuccess(`Article ${!article.is_published ? 'publié' : 'dépublié'} avec succès`);
      loadArticles();
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour:', error);
      setError(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Galerie - {getCategoryTitle()} | La Petite Académie</title>
        <meta name="description" content={`Découvrez tous les ${getCategoryTitle().toLowerCase()} de La Petite Académie`} />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        {/* Header */}
        <Box sx={{ bgcolor: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', position: 'sticky', top: 0, zIndex: 1000 }}>
          <Container maxWidth="lg" sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  component={RouterLink}
                  to="/blog"
                  startIcon={<ArrowBackIcon />}
                  variant="outlined"
                  size="small"
                >
                  Retour
                </Button>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {getCategoryTitle()}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {articles.length} article{articles.length > 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>
              
              {isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddArticle}
                  size="large"
                >
                  Nouvel article
                </Button>
              )}
            </Box>
          </Container>
        </Box>

        {/* Contenu principal */}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {articles.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                Aucun article dans cette catégorie
              </Typography>
              {isAdmin && (
                <Button variant="contained" onClick={handleAddArticle}>
                  Ajouter le premier article
                </Button>
              )}
            </Box>
          ) : (
            <Grid container spacing={3}>
              {articles.map((article) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={article.id}>
                  <Card sx={{ 
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={getImageUrl(article.image_url)}
                      alt={article.alt_text || article.title}
                      sx={{ objectFit: 'cover' }}
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Image+non+disponible';
                      }}
                    />
                    
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={article.is_published ? 'Publié' : 'Brouillon'}
                          color={article.is_published ? 'success' : 'default'}
                          size="small"
                        />
                        {article.is_featured && (
                          <Chip
                            label="À la une"
                            color="primary"
                            size="small"
                          />
                        )}
                        {article.category && (
                          <Chip
                            label={article.category}
                            color="info"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      
                      <Typography variant="h6" sx={{ 
                        fontSize: 16, 
                        fontWeight: 600, 
                        mb: 1,
                        lineHeight: 1.3
                      }}>
                        {article.title}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        fontSize: 13,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mb: 1
                      }}>
                        {article.excerpt}
                      </Typography>
                      
                      <Typography variant="caption" color="text.secondary">
                        Par {article.author} • {new Date(article.created_at).toLocaleDateString()}
                      </Typography>
                    </CardContent>

                    {isAdmin && (
                      <Box sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        gap: 0.5,
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        '&:hover': {
                          opacity: 1
                        }
                      }}>
                        <IconButton
                          onClick={() => handleTogglePublish(article)}
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': { bgcolor: 'white' }
                          }}
                          color={article.is_published ? 'success' : 'default'}
                          size="small"
                          title={article.is_published ? 'Dépublier' : 'Publier'}
                        >
                          {article.is_published ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                        <IconButton
                          onClick={() => handleEditArticle(article)}
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': { bgcolor: 'white' }
                          }}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteArticle(article.id)}
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': { bgcolor: 'white' }
                          }}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>

        {/* Bouton flottant pour admin */}
        {isAdmin && articles.length > 0 && (
          <Fab
            color="primary"
            aria-label="add"
            onClick={handleAddArticle}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000
            }}
          >
            <AddIcon />
          </Fab>
        )}

        {/* Dialog pour ajouter/modifier */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ fontWeight: 900, color: 'primary.main', fontSize: 24 }}>
            {editingItem ? 'Modifier l\'article' : 'Nouvel article'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Titre"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Extrait"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  fullWidth
                  required
                  multiline
                  rows={2}
                  helperText="Résumé de l'article qui apparaîtra sur la page blog"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Contenu complet (optionnel)"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  fullWidth
                  multiline
                  rows={6}
                  helperText="Contenu détaillé de l'article"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Auteur"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Article à la une</InputLabel>
                  <Select
                    value={formData.is_featured ? 'true' : 'false'}
                    label="Article à la une"
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.value === 'true' })}
                  >
                    <MenuItem value="false">Non</MenuItem>
                    <MenuItem value="true">Oui</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    value={formData.category}
                    label="Catégorie"
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <MenuItem value="">Sélectionner une catégorie</MenuItem>
                    <MenuItem value="Articles Généraux">Articles Généraux</MenuItem>
                    <MenuItem value="Actualités">Actualités</MenuItem>
                    <MenuItem value="Pédagogie">Pédagogie</MenuItem>
                    <MenuItem value="Événements">Événements</MenuItem>
                    <MenuItem value="Témoignages">Témoignages</MenuItem>
                    <MenuItem value="Conseils Parents">Conseils Parents</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Méthode d'ajout d'image</InputLabel>
                  <Select
                    value={uploadMethod}
                    label="Méthode d'ajout d'image"
                    onChange={(e) => setUploadMethod(e.target.value as 'upload' | 'url')}
                  >
                    <MenuItem value="url">URL d'image</MenuItem>
                    <MenuItem value="upload">Upload de fichier</MenuItem>
                  </Select>
                </FormControl>

                {uploadMethod === 'upload' ? (
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="blog-image-upload"
                      type="file"
                      onChange={handleFileSelect}
                    />
                    <label htmlFor="blog-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCameraIcon />}
                        fullWidth
                        sx={{ py: 2, borderStyle: 'dashed' }}
                      >
                        {selectedFile ? selectedFile.name : 'Sélectionner une image'}
                      </Button>
                    </label>
                  </Box>
                ) : (
                  <TextField
                    label="URL de l'image"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    fullWidth
                    required
                    placeholder="https://example.com/image.jpg"
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Texte alternatif (optionnel)"
                  value={formData.alt_text}
                  onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
            <Button
              onClick={() => setOpenDialog(false)}
              variant="outlined"
              disabled={uploading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={uploading || !formData.title || !formData.excerpt || (!formData.image_url && !selectedFile)}
            >
              {uploading ? 'En cours...' : (editingItem ? 'Mettre à jour' : 'Publier')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbars */}
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        <ContactBanner />
        <SiteFooter />
      </Box>
    </>
  );
};

export default BlogGallery;
